import type { ExportMarket } from '../../event/export/exportTypes';
import type { SearchEvent, SearchMarket } from '../api/searchTypes';

const DAY_MS = 86_400_000;
const FALLBACK_SPAN_MS = 30 * DAY_MS;

export interface MarketSpan {
    fromMs: number | null;
    toMs: number | null;
}

function time(value: string | null | undefined): number | null {
    if (!value) return null;
    const parsed = new Date(value).getTime();
    return Number.isNaN(parsed) ? null : parsed;
}

function known(values: (number | null)[]): number[] {
    return values.filter((v): v is number => v !== null);
}

export function marketSpan(
    market: SearchMarket,
    event: SearchEvent | null,
): MarketSpan {
    const own = known([
        time(market.start_date),
        time(market.event_start_date),
        time(market.effective_start_date),
    ]);
    const starts = own.length > 0 ? own : known([time(event?.start_date)]);
    const fromMs = starts.length > 0 ? Math.min(...starts) : null;

    const ends = market.is_open
        ? []
        : known([time(market.closed_date), time(market.resolved_date)]);
    const end = ends.length > 0 ? Math.max(...ends) : null;
    const toMs = end !== null && fromMs !== null && end <= fromMs ? null : end;

    return { fromMs, toMs };
}

export function exportMarketOf(
    market: SearchMarket,
    event: SearchEvent,
): ExportMarket {
    const span = marketSpan(market, event);
    return {
        conditionId: market.market_id,
        label:
            market.group_item_title ||
            market.question ||
            market.outcome_yes_label ||
            market.market_id,
        ...(span.fromMs !== null ? { fromMs: span.fromMs } : {}),
        ...(span.toMs !== null ? { toMs: span.toMs } : {}),
        event: {
            id: event.event_id,
            slug: event.slug ?? '',
            title: event.title ?? '',
        },
    };
}

export function exportMarketsOf(event: SearchEvent): ExportMarket[] {
    return event.markets.map(market => exportMarketOf(market, event));
}

export function spanBounds(
    spans: readonly { fromMs?: number | null; toMs?: number | null }[],
    nowMs: number,
): [Date, Date] {
    let start: number | null = null;
    let end: number | null = null;
    let openEnd = spans.length === 0;
    for (const span of spans) {
        if (span.fromMs != null && (start === null || span.fromMs < start)) {
            start = span.fromMs;
        }
        if (span.toMs == null) openEnd = true;
        else if (end === null || span.toMs > end) end = span.toMs;
    }
    const endMs = openEnd || end === null ? nowMs : Math.min(end, nowMs);
    const startMs =
        start !== null && start < endMs ? start : endMs - FALLBACK_SPAN_MS;
    return [new Date(startMs), new Date(endMs)];
}
